import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "@/components/seo/SEO";
import { Eyebrow, GoldButton, SectionHeading, AmbientOrbs } from "@/components/common/ui";
import {
  Bed, Sparkle, ForkKnife, Megaphone, Robot, ArrowRight, CheckCircle, Bell, CalendarBlank,
  UserCircle, MoonStars, ForkKnife as Fork, Fire, ChatCircleDots, Sparkle as SparkleI, TrendUp, ChartLineUp,
} from "@phosphor-icons/react";

/**
 * Interactive Demo — polished, animated, no video.
 * Each module is a real interactive mini-application driven by local state.
 * Cinematic transitions between steps · glass panels · real-feeling data.
 */

const MODULES = [
  { key: "front-office", en: "Front Office", nl: "Front Office", icon: Bed },
  { key: "housekeeping", en: "Housekeeping", nl: "Housekeeping", icon: Sparkle },
  { key: "restaurant", en: "Restaurant", nl: "Restaurant", icon: ForkKnife },
  { key: "marketing", en: "Marketing", nl: "Marketing", icon: Megaphone },
  { key: "ai", en: "AI Copilot", nl: "AI Copilot", icon: Robot },
];

export default function InteractiveDemo() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [active, setActive] = useState("front-office");

  const activeMeta = useMemo(() => MODULES.find((m) => m.key === active), [active]);

  return (
    <>
      <SEO
        title={lang === "nl" ? "Interactieve demo — BHT One" : "Interactive demo — BHT One"}
        path="/demo"
        description={lang === "nl" ? "Klik door BHT One zoals uw team dat elke dag zal doen — geen video, alleen live product." : "Click through BHT One like your team will every day — no video, just live product."}
      />

      <section className="relative overflow-hidden py-24 lg:py-32">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{lang === "nl" ? "Interactieve demo" : "Interactive demo"}</Eyebrow>
          <h1 className="mt-8 max-w-4xl font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            {lang === "nl" ? "Klik door BHT One zoals uw team dat elke dag zal doen." : "Click through BHT One like your team will every day."}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/65">
            {lang === "nl"
              ? "Geen video. Geen slides. Vijf modules, echte interacties, cinematic UI. Wissel modules met de rail links."
              : "No video. No slides. Five modules, real interactions, cinematic UI. Switch modules from the rail on the left."}
          </p>
        </div>
      </section>

      <section className="relative pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
            {/* Module rail */}
            <aside className="flex flex-row gap-2 overflow-x-auto lg:flex-col" role="tablist" data-testid="demo-rail">
              {MODULES.map((m) => {
                const isActive = active === m.key;
                const Icon = m.icon;
                return (
                  <button
                    key={m.key}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActive(m.key)}
                    className={`group relative flex shrink-0 items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                      isActive
                        ? "border-bht-gold/50 bg-bht-gold/10 text-bht-gold"
                        : "border-white/[0.06] bg-white/[0.02] text-white/70 hover:border-white/20 hover:text-white"
                    }`}
                    data-testid={`demo-tab-${m.key}`}
                  >
                    <Icon size={18} weight="thin" />
                    <span className="whitespace-nowrap font-serif text-[15px]">{m[lang]}</span>
                    {isActive && <span className="ml-auto hidden text-xs lg:inline">•</span>}
                  </button>
                );
              })}
              <div className="mt-4 hidden rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 lg:block">
                <div className="eyebrow">{lang === "nl" ? "Klaar voor de echte demo?" : "Ready for the real thing?"}</div>
                <p className="mt-3 text-xs text-white/55">
                  {lang === "nl"
                    ? "Een BHT solutions architect toont u live product in uw context. 30 minuten, geen slides."
                    : "A BHT solutions architect shows you live product in your context. 30 minutes, no slides."}
                </p>
                <GoldButton to={`/${lang}/book-demo`} size="sm" className="mt-4">
                  {t("cta.primary")}
                </GoldButton>
              </div>
            </aside>

            {/* Stage */}
            <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-bht-navy2/60 to-black/80" data-testid="demo-stage">
              {/* Top bar */}
              <div className="flex items-center justify-between border-b border-white/[0.06] px-6 py-3">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
                  <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/60" />
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500/60" />
                  <span className="ml-4 font-mono text-[11px] uppercase tracking-widest text-white/40">
                    bht one · {activeMeta[lang]}
                  </span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  {new Date().toLocaleDateString(lang)} · demo
                </div>
              </div>

              {/* Body */}
              <div className="min-h-[540px] px-6 py-6 md:px-8 md:py-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {active === "front-office" && <FrontOfficeDemo lang={lang} />}
                    {active === "housekeeping" && <HousekeepingDemo lang={lang} />}
                    {active === "restaurant" && <RestaurantDemo lang={lang} />}
                    {active === "marketing" && <MarketingDemo lang={lang} />}
                    {active === "ai" && <AIDemo lang={lang} />}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ================= FRONT OFFICE =================
function FrontOfficeDemo({ lang }) {
  const steps = lang === "nl"
    ? ["Zoek gast", "Verifieer ID", "Toewijzing kamer", "Mobile key"]
    : ["Find guest", "Verify ID", "Assign room", "Mobile key"];
  const [step, setStep] = useState(0);
  const rooms = [
    { n: "204", state: "ready", floor: "F2" },
    { n: "312", state: "occupied", floor: "F3" },
    { n: "418", state: "ready", floor: "F4" },
    { n: "501", state: "ready", floor: "F5" },
  ];
  const [picked, setPicked] = useState("418");

  return (
    <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
      {/* Left: check-in wizard */}
      <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6">
        <div className="mb-6 flex items-center gap-2">
          {steps.map((s, i) => (
            <div key={i} className="flex flex-1 items-center gap-2">
              <div className={`flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-mono ${i <= step ? "border-bht-gold bg-bht-gold text-bht-void" : "border-white/20 text-white/40"}`}>
                {i + 1}
              </div>
              <div className={`text-xs ${i === step ? "text-white" : "text-white/40"}`}>{s}</div>
              {i < steps.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-bht-gold/60" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4">
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
              <input
                readOnly
                value="Silvia Kowalski"
                className="w-full bg-transparent font-serif text-2xl text-white focus:outline-none"
              />
              <div className="mt-2 text-xs text-white/50">Deluxe King · 2 nights · silver tier</div>
            </div>
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 text-sm text-white/70">
              <div className="eyebrow mb-2">{lang === "nl" ? "Herkend" : "Recognised"}</div>
              <div>{lang === "nl" ? "Terugkerende gast · 4e bezoek · voorkeur voor stille kamer" : "Returning guest · 4th visit · prefers quiet room"}</div>
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4">
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="eyebrow mb-3">{lang === "nl" ? "Document" : "Document"}</div>
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-24 items-center justify-center rounded-md border border-bht-gold/40 bg-gradient-to-br from-bht-navy to-bht-void">
                  <UserCircle size={24} className="text-bht-gold" />
                </div>
                <div className="text-sm">
                  <div className="text-white">Passport · NL · X••••••3421</div>
                  <div className="mt-1 text-xs text-white/50">{lang === "nl" ? "AI-geparst in 0,4s · verified" : "AI-parsed in 0.4s · verified"}</div>
                </div>
                <CheckCircle size={20} weight="fill" className="ml-auto text-bht-gold" />
              </div>
            </div>
          </div>
        )}
        {step === 2 && (
          <div>
            <div className="eyebrow mb-3">{lang === "nl" ? "Kies kamer" : "Choose room"}</div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {rooms.map((r) => (
                <button
                  key={r.n}
                  onClick={() => setPicked(r.n)}
                  className={`rounded-lg border p-3 text-left transition-colors ${picked === r.n ? "border-bht-gold/60 bg-bht-gold/10" : r.state === "ready" ? "border-white/10 bg-white/[0.02] hover:border-white/25" : "cursor-not-allowed border-white/[0.04] bg-white/[0.01] opacity-40"}`}
                  disabled={r.state !== "ready"}
                >
                  <div className="font-serif text-xl text-white">{r.n}</div>
                  <div className="mt-1 text-[10px] font-mono tracking-widest text-white/40 uppercase">{r.floor} · {r.state}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="flex h-24 w-24 items-center justify-center rounded-2xl border-2 border-bht-gold bg-bht-gold/10"
            >
              <SparkleI size={38} weight="fill" className="text-bht-gold" />
            </motion.div>
            <div className="mt-6 font-serif text-2xl">{lang === "nl" ? "Mobile key verstuurd" : "Mobile key delivered"}</div>
            <div className="mt-2 max-w-sm text-sm text-white/55">
              {lang === "nl"
                ? `Kamer ${picked} · sleutel actief tot check-out · gast is onderweg.`
                : `Room ${picked} · key active until check-out · guest is on their way.`}
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            className="text-sm text-white/50 hover:text-bht-gold disabled:opacity-30"
            disabled={step === 0}
            data-testid="demo-fo-prev"
          >
            ← {lang === "nl" ? "Terug" : "Back"}
          </button>
          <button
            onClick={() => setStep(Math.min(steps.length - 1, step + 1))}
            className="inline-flex items-center gap-2 rounded-full bg-bht-gold px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-bht-void hover:scale-105"
            data-testid="demo-fo-next"
          >
            {step === steps.length - 1 ? (lang === "nl" ? "Opnieuw" : "Restart") : (lang === "nl" ? "Volgende" : "Next")}
            <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {/* Right: live stats */}
      <div className="space-y-4">
        <MiniPanel label="Check-ins today" value="42" trend="+12%" />
        <MiniPanel label={lang === "nl" ? "Gem. check-in tijd" : "Avg. check-in time"} value="28s" trend="-64%" />
        <MiniPanel label={lang === "nl" ? "Upsell-ratio" : "Upsell rate"} value="18%" trend="+3.4pp" />
        <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-5">
          <div className="eyebrow mb-4">{lang === "nl" ? "Arrivals · vandaag" : "Arrivals · today"}</div>
          <div className="space-y-3">
            {[
              { t: "14:20", n: "Kowalski", r: "418" },
              { t: "15:40", n: "Bergström", r: "312" },
              { t: "16:15", n: "Ivanov", r: "501" },
            ].map((a) => (
              <div key={a.t} className="flex items-center justify-between text-sm">
                <span className="font-mono text-xs text-white/50">{a.t}</span>
                <span className="text-white/85">{a.n}</span>
                <span className="rounded border border-white/10 px-2 py-0.5 font-mono text-[10px] text-white/60">{a.r}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ================= HOUSEKEEPING =================
function HousekeepingDemo({ lang }) {
  const [rooms, setRooms] = useState([
    { n: "104", status: "dirty", att: null },
    { n: "205", status: "in_progress", att: "Maria" },
    { n: "212", status: "clean", att: "Ana" },
    { n: "308", status: "dirty", att: null },
    { n: "410", status: "dirty", att: null },
    { n: "501", status: "clean", att: "Elena" },
  ]);
  const stateColor = (s) => ({
    dirty: "bg-white/[0.03] border-red-500/30 text-red-300",
    in_progress: "bg-bht-gold/10 border-bht-gold/40 text-bht-gold",
    clean: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  }[s]);

  const dispatch = () => {
    setRooms((r) => {
      let flipped = 0;
      const attendants = ["Ana", "Maria", "Elena", "Jorge"];
      return r.map((x) => {
        if (x.status === "dirty" && flipped < 3) {
          const att = attendants[flipped % attendants.length];
          flipped += 1;
          return { ...x, status: "in_progress", att };
        }
        return x;
      });
    });
  };
  const complete = () => {
    setRooms((r) => r.map((x) => x.status === "in_progress" ? { ...x, status: "clean" } : x));
  };
  const reset = () => {
    setRooms([
      { n: "104", status: "dirty", att: null },
      { n: "205", status: "in_progress", att: "Maria" },
      { n: "212", status: "clean", att: "Ana" },
      { n: "308", status: "dirty", att: null },
      { n: "410", status: "dirty", att: null },
      { n: "501", status: "clean", att: "Elena" },
    ]);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
      <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="eyebrow">{lang === "nl" ? "Kamerstatus" : "Room board"}</div>
          <div className="flex items-center gap-2">
            <button onClick={dispatch} className="rounded-full border border-bht-gold/40 bg-bht-gold/10 px-3 py-1.5 text-[11px] uppercase tracking-widest text-bht-gold hover:bg-bht-gold/20" data-testid="demo-hk-dispatch">AI Dispatch</button>
            <button onClick={complete} className="rounded-full border border-white/10 px-3 py-1.5 text-[11px] uppercase tracking-widest text-white/70 hover:border-white/25" data-testid="demo-hk-complete">Complete</button>
            <button onClick={reset} className="rounded-full border border-white/10 px-3 py-1.5 text-[11px] uppercase tracking-widest text-white/50 hover:border-white/20" data-testid="demo-hk-reset">Reset</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {rooms.map((r) => (
            <motion.div
              layout
              key={r.n}
              className={`rounded-xl border p-4 ${stateColor(r.status)}`}
              data-testid={`demo-hk-room-${r.n}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-serif text-xl text-white">{r.n}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest opacity-70">{r.status.replace("_", " ")}</div>
              </div>
              <div className="mt-3 text-xs text-white/70">{r.att ? `${lang === "nl" ? "Toegewezen" : "Assigned"} · ${r.att}` : (lang === "nl" ? "Niet toegewezen" : "Unassigned")}</div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <MiniPanel label={lang === "nl" ? "Vandaag te reinigen" : "Rooms to clean"} value={String(rooms.filter((r) => r.status !== "clean").length)} />
        <MiniPanel label={lang === "nl" ? "Voltooid" : "Cleaned"} value={String(rooms.filter((r) => r.status === "clean").length)} />
        <MiniPanel label="SLA" value="99.4%" trend="+2.1pp" />
        <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-5">
          <div className="eyebrow mb-3">{lang === "nl" ? "Attendants · live" : "Attendants · live"}</div>
          {["Ana", "Maria", "Elena", "Jorge"].map((n, i) => (
            <div key={n} className="flex items-center justify-between border-b border-white/[0.04] py-2 last:border-0 text-sm">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                <span className="text-white/85">{n}</span>
              </div>
              <span className="font-mono text-xs text-white/50">{6 - i} {lang === "nl" ? "kamers" : "rooms"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ================= RESTAURANT =================
function RestaurantDemo({ lang }) {
  const [tables, setTables] = useState([
    { n: "T1", state: "seated", covers: 2, elapsed: 24 },
    { n: "T2", state: "ordering", covers: 4, elapsed: 6 },
    { n: "T3", state: "eating", covers: 2, elapsed: 38 },
    { n: "T4", state: "empty", covers: 0, elapsed: 0 },
    { n: "T5", state: "seated", covers: 3, elapsed: 12 },
    { n: "T6", state: "eating", covers: 6, elapsed: 62 },
  ]);
  const [order, setOrder] = useState([]);
  const menu = [
    { n: "Sea bass ceviche", p: 18, allergens: ["fish"] },
    { n: "Gnocchi al burro", p: 22, allergens: ["gluten", "dairy"] },
    { n: "Wagyu, aged 40d", p: 68, allergens: [] },
    { n: "Roasted celeriac", p: 16, allergens: [] },
  ];
  const [allergyOn, setAllergyOn] = useState(true);

  const toggleAdd = (item) => setOrder((o) => o.find((x) => x.n === item.n) ? o.filter((x) => x.n !== item.n) : [...o, item]);

  const total = order.reduce((s, x) => s + x.p, 0);
  const conflicting = allergyOn && order.some((x) => x.allergens.includes("dairy"));

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.15fr]">
      <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6">
        <div className="eyebrow mb-4">{lang === "nl" ? "Tafeloverzicht" : "Floor plan"}</div>
        <div className="grid grid-cols-3 gap-3">
          {tables.map((t) => (
            <div key={t.n} className={`rounded-xl border p-3 ${t.state === "empty" ? "border-white/10 bg-white/[0.02]" : "border-bht-gold/30 bg-bht-gold/5"}`}>
              <div className="flex items-center justify-between">
                <div className="font-serif text-lg text-white">{t.n}</div>
                <Fork size={14} className={t.state === "empty" ? "text-white/30" : "text-bht-gold"} />
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/50">{t.state}</div>
              {t.state !== "empty" && (
                <div className="mt-2 text-xs text-white/60">{t.covers}p · {t.elapsed}m</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="eyebrow">T2 · {lang === "nl" ? "Bestelling" : "Order"}</div>
          <label className="flex items-center gap-2 text-xs text-white/60">
            <input type="checkbox" checked={allergyOn} onChange={(e) => setAllergyOn(e.target.checked)} data-testid="demo-rest-allergy-toggle" />
            <span>{lang === "nl" ? "Allergenen-check" : "Allergen check"}</span>
          </label>
        </div>
        <div className="mb-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 text-xs text-white/60">
          {lang === "nl" ? "Gast van T2 heeft lactose-intolerantie" : "T2 guest is lactose intolerant"}
        </div>
        <div className="space-y-2">
          {menu.map((m) => {
            const conflict = allergyOn && m.allergens.includes("dairy");
            const added = !!order.find((x) => x.n === m.n);
            return (
              <button
                key={m.n}
                onClick={() => !conflict && toggleAdd(m)}
                disabled={conflict}
                className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors ${
                  conflict ? "cursor-not-allowed border-red-500/30 bg-red-500/5 opacity-70" :
                  added ? "border-bht-gold/50 bg-bht-gold/10" : "border-white/[0.06] bg-white/[0.02] hover:border-white/20"
                }`}
                data-testid={`demo-rest-item-${m.n.split(' ')[0].toLowerCase()}`}
              >
                <div>
                  <div className="text-sm text-white">{m.n}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-white/40">
                    {m.allergens.length ? m.allergens.join(" · ") : (lang === "nl" ? "geen allergenen" : "no allergens")}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-white/70">€ {m.p}</span>
                  {conflict && <span className="rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-red-300">block</span>}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-white/[0.06] pt-4">
          <div className="eyebrow">Total</div>
          <div className="font-serif text-2xl text-bht-gold">€ {total}</div>
        </div>
      </div>
    </div>
  );
}

// ================= MARKETING =================
function MarketingDemo({ lang }) {
  const [seg, setSeg] = useState("returning");
  const segs = [
    { k: "returning", en: "Returning guests", nl: "Terugkerende gasten", n: 1284 },
    { k: "vip", en: "VIP tier", nl: "VIP-tier", n: 342 },
    { k: "lapsed", en: "Lapsed 12mo+", nl: "Inactief 12mo+", n: 2141 },
    { k: "birthday", en: "Birthday this month", nl: "Jarig deze maand", n: 128 },
  ];
  const active = segs.find((s) => s.k === seg);

  const subject = seg === "returning" ? "A quieter suite is waiting for you" :
    seg === "vip" ? "Your usual, elevated" :
    seg === "lapsed" ? "It's been a while — a room on us." :
    "Happy birthday from all of us";

  return (
    <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
      <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6">
        <div className="eyebrow mb-3">{lang === "nl" ? "Segmenten" : "Segments"}</div>
        <div className="space-y-2">
          {segs.map((s) => (
            <button
              key={s.k}
              onClick={() => setSeg(s.k)}
              className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors ${
                seg === s.k ? "border-bht-gold/50 bg-bht-gold/10" : "border-white/[0.06] bg-white/[0.02] hover:border-white/20"
              }`}
              data-testid={`demo-mkt-seg-${s.k}`}
            >
              <span className="text-sm text-white">{s[lang]}</span>
              <span className="font-mono text-xs text-white/50">{s.n.toLocaleString(lang)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6">
        <div className="eyebrow mb-3">{lang === "nl" ? "Campagne-preview" : "Campaign preview"}</div>
        <motion.div key={seg} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="rounded-xl border border-white/[0.06] bg-gradient-to-b from-white/[0.02] to-transparent p-5">
            <div className="flex items-center gap-2 text-xs text-white/50">
              <span className="rounded border border-white/10 bg-black/40 px-1.5 py-0.5 font-mono text-[10px] tracking-widest">FROM</span>
              <span>concierge@bhtsolutions.nl</span>
            </div>
            <div className="mt-3 font-serif text-2xl text-white">{subject}</div>
            <p className="mt-4 text-sm leading-relaxed text-white/65">
              {lang === "nl"
                ? `Dag ${active[lang].split(' ')[0]}, we hebben iets bijzonders voor u. Uw voorkeuren staan in ons systeem — deze aanbieding is op maat.`
                : `Hello ${active[lang].split(' ')[0]}, we set something special aside for you. Your preferences are on file — this offer is tailored.`}
            </p>
            <div className="mt-6 flex gap-3">
              <span className="rounded-full bg-bht-gold px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-bht-void">Book now</span>
              <span className="rounded-full border border-white/10 px-4 py-2 text-[11px] uppercase tracking-widest text-white/70">Later</span>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <SmallStat k={lang === "nl" ? "Verwachte opens" : "Predicted opens"} v={`${Math.round(0.38 * active.n)}`} />
            <SmallStat k={lang === "nl" ? "Verwachte clicks" : "Predicted clicks"} v={`${Math.round(0.11 * active.n)}`} />
            <SmallStat k={lang === "nl" ? "Verwachte revenue" : "Predicted revenue"} v={`€ ${(active.n * 12).toLocaleString(lang)}`} />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ================= AI COPILOT =================
function AIDemo({ lang }) {
  const chat = lang === "nl"
    ? [
      { role: "user", t: "Welke kamers zijn morgen te laat schoon voor 15:00 check-in?" },
      { role: "assistant", t: "Op basis van housekeeping-schema en attendant-load lopen 205, 308 en 501 risico op late turnover. Wilt u nu 2 extra attendants voor vroege dienst 07:30 inplannen? Verwachte impact: alle drie kamers klaar om 14:40." },
      { role: "user", t: "Ja, plan in en informeer de attendants." },
      { role: "assistant", t: "Gedaan. Ana en Jorge staan ingepland voor 07:30. WhatsApp-bericht verstuurd. Guest recovery pack toegevoegd aan de room-attendant checklists." },
    ]
    : [
      { role: "user", t: "Which rooms are at risk of being late for 3PM check-in tomorrow?" },
      { role: "assistant", t: "Based on housekeeping schedule and attendant load, 205, 308 and 501 risk late turnover. Want me to schedule 2 extra attendants for the 07:30 shift? Projected impact: all three rooms ready by 14:40." },
      { role: "user", t: "Yes, schedule and notify the attendants." },
      { role: "assistant", t: "Done. Ana and Jorge scheduled for 07:30. WhatsApp sent. Guest recovery pack added to their room-attendant checklists." },
    ];
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    if (visible >= chat.length) return;
    const t = setTimeout(() => setVisible((v) => v + 1), 900);
    return () => clearTimeout(t);
  }, [visible, chat.length]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
      <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6">
        <div className="eyebrow mb-4">Ask BHT AI · demo session</div>
        <div className="max-h-[420px] space-y-4 overflow-y-auto pr-2">
          {chat.slice(0, visible).map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${
                m.role === "user" ? "bg-bht-gold text-bht-void" : "bg-white/[0.04] text-white/90"
              }`}>
                {m.t}
              </div>
            </motion.div>
          ))}
          {visible < chat.length && (
            <div className="flex items-center gap-2 text-xs text-white/40">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-bht-gold" />
              <span>Thinking</span>
            </div>
          )}
        </div>
        <div className="mt-6">
          <button
            onClick={() => setVisible(0)}
            className="rounded-full border border-white/10 px-4 py-2 text-[11px] uppercase tracking-widest text-white/60 hover:border-white/25"
            data-testid="demo-ai-replay"
          >
            {lang === "nl" ? "Herhaal demo" : "Replay demo"}
          </button>
        </div>
      </div>
      <div className="space-y-4">
        <MiniPanel label={lang === "nl" ? "Automatiseringen vandaag" : "Automations today"} value="1,284" trend="+12%" />
        <MiniPanel label={lang === "nl" ? "Uren bespaard per week" : "Staff hours saved / week"} value="62h" />
        <MiniPanel label={lang === "nl" ? "Gast-sentiment" : "Guest sentiment"} value="+18" trend="↑" />
        <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-5">
          <div className="eyebrow mb-3">{lang === "nl" ? "Voorstellen" : "Suggestions"}</div>
          {[
            lang === "nl" ? "Signaleer VIP-arrivals aan de F&B-manager" : "Alert F&B manager on VIP arrivals",
            lang === "nl" ? "Genereer weekly executive digest" : "Generate weekly executive digest",
            lang === "nl" ? "Auto-response op review < 3★" : "Auto-response for reviews < 3★",
          ].map((s, i) => (
            <div key={i} className="mb-2 flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-xs text-white/75">
              <SparkleI size={12} className="text-bht-gold" />
              <span className="flex-1">{s}</span>
              <span className="text-white/40">→</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==== helpers ====
function MiniPanel({ label, value, trend }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-black/40 p-5" data-testid="demo-mini">
      <div className="eyebrow">{label}</div>
      <div className="mt-3 flex items-baseline justify-between">
        <span className="font-serif text-3xl leading-none text-white">{value}</span>
        {trend && <span className="font-mono text-[11px] tracking-widest text-bht-gold">{trend}</span>}
      </div>
    </div>
  );
}

function SmallStat({ k, v }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="font-serif text-lg text-white">{v}</div>
      <div className="mt-1 eyebrow text-[9px]">{k}</div>
    </div>
  );
}
